import { useCallback } from 'react';
import { AddCommentPayload, UpdateCommentPayload } from '@sdk/newsfeed/types/apiTypes';

import { ArticleComment } from '@web/newsfeed/types/Article';
import { useRemoveCommentLike } from '@web/newsfeed/Article/ArticleComments/hooks/useRemoveCommentLike';
import { useAddCommentLike } from '@web/newsfeed/Article/ArticleComments/hooks/useAddCommentLike';
import { useDeleteComment } from '@web/newsfeed/Article/ArticleComments/hooks/useDeleteComment';
import { useAddComment } from '@web/newsfeed/Article/ArticleComments/hooks/useAddComment';
import { useEditComment } from '@web/newsfeed/Article/ArticleComments/hooks/useEditComment';

type ArticleCommentProps = {
  pageId: number;
  articleId: number;
  commentId: number;
};

export const useCommentActions = () => {
  const addCommentLikeMutation = useAddCommentLike();
  const removeCommentLikeMutation = useRemoveCommentLike();
  const deleteCommentMutation = useDeleteComment();
  const addCommentMutation = useAddComment();
  const editCommentMutation = useEditComment();

  const addComment = useCallback(
    async (payload: AddCommentPayload) => {
      await addCommentMutation.mutateAsync(payload);
    },
    [addCommentMutation]
  );

  const addCommentLike = useCallback(
    async (payload: ArticleCommentProps) => {
      await addCommentLikeMutation.mutateAsync(payload);
    },
    [addCommentLikeMutation]
  );

  const deleteComment = useCallback(
    async (payload: ArticleCommentProps) => {
      await deleteCommentMutation.mutateAsync(payload);
    },
    [deleteCommentMutation]
  );

  const editComment = useCallback(
    async (payload: UpdateCommentPayload) => {
      await editCommentMutation.mutateAsync(payload);
    },
    [editCommentMutation]
  );

  const removeCommentLike = useCallback(
    async (payload: ArticleCommentProps) => {
      await removeCommentLikeMutation.mutateAsync(payload);
    },
    [removeCommentLikeMutation]
  );

  const toggleCommentLike = useCallback(
    (comment: ArticleComment) => {
      if (comment.likedByMe) {
        removeCommentLike({
          pageId: comment.pageId,
          articleId: comment.articleId,
          commentId: comment.id,
        });
      } else {
        addCommentLike({
          pageId: comment.pageId,
          articleId: comment.articleId,
          commentId: comment.id,
        });
      }
    },
    [addCommentLike, removeCommentLike]
  );

  return { addComment, deleteComment, editComment, toggleCommentLike };
};
